import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skel } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { deviceInfo } from "@/lib/assessments";
import { cn } from "@/lib/utils";

function useTabSwitches() {
  const n = useRef(0);
  useEffect(() => {
    const h = () => { if (document.visibilityState === "hidden") n.current += 1; };
    document.addEventListener("visibilitychange", h);
    return () => document.removeEventListener("visibilitychange", h);
  }, []);
  return () => { const v = n.current; n.current = 0; return v; };
}

const Likert = ({ item, labels, value, onPick }) => (
  <div className="panel space-y-3 p-4 sm:p-5" data-testid={`item-${item.id}`}>
    <p className="text-base font-medium text-ink">{item.text}</p>
    <div className="grid grid-cols-5 gap-1.5">
      {labels.map((l, i) => (
        <button key={l} type="button" onClick={() => onPick(i + 1)} data-testid={`likert-${item.id}-${i + 1}`} aria-pressed={value === i + 1}
          className={cn("flex min-h-[52px] flex-col items-center justify-center rounded-lg border px-1 py-2 text-[11px] leading-tight transition-colors duration-150 sm:text-xs",
            value === i + 1 ? "border-brand bg-brand text-white" : "border-line bg-white text-ink-secondary hover:border-brand/40")}>
          <span className="font-display text-sm font-semibold">{i + 1}</span><span className="mt-0.5 text-center">{l}</span>
        </button>
      ))}
    </div>
  </div>
);

export function AssessItems({ token, info, onFinished }) {
  const { data } = useQuery({ queryKey: ["assess-items", token], queryFn: () => get(`/public/assess/${token}/items`), staleTime: Infinity });
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const times = useRef({});
  const last = useRef(Date.now());
  const pageStart = useRef(Date.now());
  const takeSwitches = useTabSwitches();

  useEffect(() => {
    if (!data) return;
    if (!data.pages.length) {  // no Personality section in this assessment
      if (info?.extra_pre) onFinished("extra-pre");
      else if (data.sjt.length) onFinished("sjt");
      else if (info?.extra_count) onFinished("extra");
      else api.post(`/public/assess/${token}/submit`).then(() => onFinished("done")).catch((e) => toast.error(errMsg(e)));
      return;
    }
    setAnswers(data.answers);
    const first = data.pages.findIndex((p) => p.some((it) => !data.answers[it.id]));
    setPage(first === -1 ? data.pages.length - 1 : first);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  useEffect(() => { last.current = Date.now(); pageStart.current = Date.now(); window.scrollTo({ top: 0 }); }, [page]);

  if (!data || page == null) return <Skel className="h-96" />;
  const items = data.pages[page];
  const total = data.pages.flat().length;
  const done = Object.keys(answers).length;
  const pick = (id, v) => { times.current[id] = Date.now() - last.current; last.current = Date.now(); setAnswers((a) => ({ ...a, [id]: v })); };

  const next = async () => {
    const missing = items.filter((it) => !answers[it.id]);
    if (missing.length) { toast.error(`Please answer all ${items.length} statements on this page.`); document.querySelector(`[data-testid="item-${missing[0].id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    setSaving(true);
    const responses = Object.fromEntries(items.map((it) => [it.id, { value: answers[it.id], ms: times.current[it.id] ?? 0 }]));
    try {
      await api.post(`/public/assess/${token}/page`, { page: page + 1, responses, tab_switches: takeSwitches(), elapsed_ms: Date.now() - pageStart.current, device: deviceInfo() });
      if (page + 1 < data.pages.length) setPage(page + 1);
      else if (info?.extra_pre) onFinished("extra-pre");
      else if (data.sjt.length) onFinished("sjt");
      else if (info?.extra_count) onFinished("extra");
      else { await api.post(`/public/assess/${token}/submit`); onFinished("done"); }
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="assess-items">
      <div className="sticky top-0 z-10 -mx-4 space-y-2 bg-canvas/95 px-4 py-3 sm:mx-0 sm:px-0">
        <div className="flex justify-between text-xs text-ink-muted"><span data-testid="assess-page-label">Page {page + 1} of {data.pages.length}</span><span data-testid="assess-progress-label">{done} of {total} answered</span></div>
        <Progress value={(100 * done) / total} className="h-2 bg-brand-tint" data-testid="assess-progress" />
      </div>
      <p className="text-sm text-ink-secondary">How well does each statement describe you? Answer the way you usually are.</p>
      {items.map((it) => <Likert key={it.id} item={it} labels={data.likert} value={answers[it.id]} onPick={(v) => pick(it.id, v)} />)}
      <div className="flex items-center justify-between gap-3 pt-2">
        <Button variant="ghost" disabled={page === 0 || saving} onClick={() => setPage(page - 1)} data-testid="assess-prev-btn">Back</Button>
        <Button className="h-12 flex-1 text-base sm:flex-none sm:px-10" disabled={saving} onClick={next} data-testid="assess-next-btn">
          {saving && <Loader2 size={16} className="animate-spin" />}{page + 1 < data.pages.length ? "Save and continue" : data.sjt.length ? "Continue to scenarios" : "Submit"}
        </Button>
      </div>
      <p className="text-center text-xs text-ink-muted">Your answers save automatically on every page.</p>
    </div>
  );
}
